import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {MipLevelRegion, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {ReductionHelperHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/reduction-helper-hal"
import {TiledReductionHelper} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/tiled-reduction-helper-hal"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/reduce/definition"
import {determineDescriptor} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/reduce/helpers"
import {HalImage} from "@cm/hal"
import {hasBatching} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/utils/image-op-utils"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {AnyImageOpHalData, CtorArgs, ImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UPDATE_COMPLEXITY_HIGH} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

type Definition = typeof definition

export class ImageOpReduceHal extends ImageOpHal<Definition, ImageOpReduceHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpReduceHalData>): ImageOpReduceHalData {
        return new ImageOpReduceHalData({
            ...args,
            descriptor: determineDescriptor(args.parameters.sourceImage.descriptor, args.parameters.custom?.reduce.resultChannels),
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: UPDATE_COMPLEXITY_HIGH,
        })
    }
}

class ImageOpReduceHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpReduceHalData>) {
        super(args)
        const {operator, custom} = this.parameters
        // A source's batching status can only change via its own parameters changing, which propagates a
        // parameter-change notification to every consumer (see ImageOpHal.notifyParameterChange) and forces
        // this data instance to be recreated - so it's safe to read it once here rather than on every
        // writeRegion call. writeRegion re-derives it and asserts it still matches, as a safety net against
        // that invariant ever turning out to be wrong.
        this.isBatched = hasBatching(this.parameters.sourceImage.descriptor)
        // custom (position-dependent preprocess risk) and patch-grid (batched) sources - where each patch
        // needs normalizing by its own, possibly edge-clipped, pixel count, only expressible via
        // ReductionHelperHal's own per-patch postProcess - keep using a single unchunked full-region fetch.
        // Everything else is chunked/cached via TiledReductionHelper.
        this.useRealOperator = operator === "custom" || this.isBatched
        if (this.useRealOperator) {
            this.reductionHelper = new ReductionHelperHal(this.ctx.halContext, {operator, custom})
            this.tiledReductionHelper = null
        } else {
            this.reductionHelper = null
            // operator !== "custom" is guaranteed here by `useRealOperator`'s definition above
            this.tiledReductionHelper = new TiledReductionHelper(this.ctx, [{operator: operator as Exclude<typeof operator, "custom">}])
        }
    }

    protected override dispose() {
        this.reductionHelper?.dispose()
        this.tiledReductionHelper?.dispose()
        super.dispose()
    }

    protected override async writeRegion(outputImage: HalImage, _mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        const {sourceImage, options: reduceOptions} = this.parameters
        sourceImage.addRef("Reduce.writeRegion - sourceImage")
        try {
            if (hasBatching(sourceImage.descriptor) !== this.isBatched) {
                throw new Error("opReduce: source batching status changed without this data instance being recreated")
            }
            if (this.reductionHelper) {
                const fullSourceHalImage = await sourceImage.getUpdatedHalImage({
                    x: 0,
                    y: 0,
                    width: sourceImage.descriptor.width,
                    height: sourceImage.descriptor.height,
                    mipLevel: 0,
                })
                this.reductionHelper.reduce({
                    source: fullSourceHalImage.halImage,
                    target: outputImage,
                    options: {
                        numPatches: sourceImage.descriptor.batching?.numPatches,
                        patchSize: sourceImage.descriptor.batching?.patchSize,
                        patchFillAddressMode: reduceOptions?.patchFillAddressMode,
                    },
                })
                fullSourceHalImage.release()
            } else {
                await this.tiledReductionHelper!.reduceAll({sourceImage, targets: [outputImage], options})
            }
        } finally {
            sourceImage.release("Reduce.writeRegion - sourceImage")
        }
    }

    protected override onSourceRegionsInvalidated(_sourceImage: AnyImageOpHalData, _sourceRegions: Regions) {
        this.invalidateRegions() // always invalidate the entire result when the source changes
    }

    private readonly reductionHelper: ReductionHelperHal | null
    private readonly tiledReductionHelper: TiledReductionHelper | null
    private readonly isBatched: boolean
    private readonly useRealOperator: boolean
}
